import { useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, Loader2, ImageIcon, X, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RoofPhotoUpload() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    setFiles((prev) => [...prev, ...picked]);
    setDone(false);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please add your name so we know whose roof it is.");
    if (files.length === 0) return toast.error("Please choose at least one photo of your roof.");
    setLoading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("name", name);
        fd.append("phone", phone);
        fd.append("site_url", process.env.REACT_APP_BACKEND_URL);
        await axios.post(`${API}/roof-photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded! We'll review your roof damage shortly.`);
      setFiles([]);
      setName("");
      setPhone("");
      setDone(true);
    } catch (err) {
      toast.error("Upload failed. Please try again or call 443-250-9046.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid="roof-upload-box"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className="relative bg-hero-surface border border-white/10 rounded-lg p-8 md:p-12"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-hero-red mb-4">— Show Us The Damage</p>
          <h2 className="font-display uppercase text-3xl sm:text-5xl leading-[0.95]">
            Upload photos<br />of your roof.
          </h2>
        </div>
        <p className="max-w-sm text-hero-silver">Snap a few pictures of the damage and send them over. Our crew will take a look and reach out about your free inspection.</p>
      </div>

      <form onSubmit={submit} data-testid="roof-upload-form" className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label htmlFor="up-name" className="text-white/80">Your Name</Label>
            <Input id="up-name" data-testid="upload-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 focus-visible:ring-hero-red focus-visible:border-hero-red" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="up-phone" className="text-white/80">Phone (optional)</Label>
            <Input id="up-phone" data-testid="upload-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(443) 250-9046" className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 focus-visible:ring-hero-red focus-visible:border-hero-red" />
          </div>
        </div>

        <input ref={inputRef} type="file" accept="image/*" multiple onChange={onPick} className="hidden" data-testid="upload-file-input" />

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
          className="group w-full border-2 border-dashed border-white/20 hover:border-hero-red rounded-lg py-12 flex flex-col items-center justify-center gap-3 transition-colors duration-200"
        >
          <UploadCloud className="h-10 w-10 text-hero-silver group-hover:text-hero-red transition-colors duration-200" />
          <span className="text-white font-semibold">Click to choose roof photos</span>
          <span className="text-sm text-hero-silver">JPG or PNG • up to 8MB each</span>
        </button>

        {files.length > 0 && (
          <div className="flex flex-wrap gap-3" data-testid="upload-preview-list">
            {files.map((f, i) => (
              <div key={i} className="relative group flex items-center gap-2 bg-white/5 border border-white/10 rounded-md pl-3 pr-2 py-2">
                <ImageIcon className="h-4 w-4 text-hero-red shrink-0" />
                <span className="text-sm text-white/80 max-w-[160px] truncate">{f.name}</span>
                <button type="button" onClick={() => removeFile(i)} data-testid={`remove-file-${i}`} className="text-white/40 hover:text-hero-red transition-colors duration-200">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="upload-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-hero-red hover:bg-hero-reddark disabled:opacity-60 text-white font-bold uppercase tracking-wide text-sm py-4 rounded-md transition-colors duration-200"
        >
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : done ? <><CheckCircle2 className="h-4 w-4" /> Uploaded — Send More</> : "Upload Roof Photos"}
        </button>
      </form>
    </motion.div>
  );
}
